import { ColDef } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { canEditPricelists } from '../../utils/permissions';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgNumberFilter from '../../components/common/customAgGrid/CustomAgNumberFilter';
import Stack from '@mui/material/Stack';
import PageHeader, { ActionItem } from '../../components/PageHeader';
import AddAndEditPriceModal from '../../components/priceLists/AddAndEditPriceModal';
import PriceListDetailsInformation from '../../components/priceLists/PriceListDetailsInformation';
import { useGetPortLocationsQuery } from '../../store/apiSlices/ports/portsApis';
import {
  useAddPriceListProductMutation,
  useLazyGetPriceListProductsQuery,
  useUpdatePriceListProductMutation,
} from '../../store/apiSlices/priceLists/priceListApis';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { useGetActiveProductsBySupplierQuery } from '../../store/apiSlices/lubricationChart/lubricationChartsApis';
import { TableCardContent } from '../../components/common/styled/TableCardContent';

const PriceListDetailContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddProductPrice = canEditPricelists(userInfo?.authorityName);

  const priceListInfo = location.state?.data;
  const priceListId = priceListInfo?.id || priceListInfo?.srno;
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({
    productId: '',
    portId: '',
    price: '',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      productId: selectedProduct?.productId || '',
      portId: selectedProduct?.portId || '',
      price: selectedProduct?.price || '',
    },
  });

  const breadcrumbOptions = [
    {
      label: t('priceList.title'),
      isActive: false,
      onClick: () => {
        navigate('/price-list');
      },
    },
    {
      label: t('common.details'),
      isActive: true,
      onClick: () => {},
    },
  ];

  // Only roles allowed to edit price lists can add product prices
  const actions: ActionItem[] = !canAddProductPrice
    ? []
    : [
        {
          label: t('priceList.addProductPrice'),
          onClick: () => {
            setIsEditMode(false);
            setSelectedProduct({ productId: '', portId: '', price: '' });
            reset({ productId: '', portId: '', price: '' });
            setOpenAddEditModal(true);
          },
          variant: 'blue',
        },
      ];

  const columns: ColDef[] = [
    {
      field: 'productName',
      headerName: t('table.columns.productName'),
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      width: 200,
      floatingFilterComponent: CustomAgMultiSelectFilter,
    },
    {
      field: 'port',
      headerName: t('table.columns.port'),
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      width: 200,
      floatingFilterComponent: CustomAgMultiSelectFilter,
    },
    {
      field: 'price',
      headerName: t('table.columns.price'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      width: 200,
      floatingFilterComponent: CustomAgNumberFilter,
    },
    // {
    //     field: "action",
    //     headerName: "",
    //     sortable: false,
    //     filter: false,
    //     width: 50,
    //     maxWidth: 50,
    //     flex: 0,
    //     pinned: 'right',
    //     cellRenderer: (params: any) => (
    //         <CustomActionButton menuOptions={createMenuOptions(params.data)} />
    //     )
    // }
  ];

  const [fetchProducts, { data: rowData = [], isLoading }] = useLazyGetPriceListProductsQuery();

  // Options for product and port dropdowns
  // Use supplier-specific products from the price list
  const supplierId = priceListInfo?.supplierId;
  const { data: productsResp } = useGetActiveProductsBySupplierQuery(
    { supplierId: supplierId || 0 },
    {
      skip: !supplierId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const productOptions = (() => {
    const respData: any = productsResp?.data;
    const arr = Array.isArray(respData) ? respData : respData?.content || [];
    if (!Array.isArray(arr)) return [];
    return arr.map((p: any) => ({ value: p.id, label: p.name }));
  })();

  const { data: portOptionsData } = useGetPortLocationsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const portOptions = Array.isArray(portOptionsData) ? portOptionsData : [];

  const [addPrice] = useAddPriceListProductMutation();
  const [updatePrice, { isLoading: isSubmitting }] = useUpdatePriceListProductMutation();

  useEffect(() => {
    if (priceListId) {
      fetchProducts({ priceListId });
    }
  }, [priceListId, fetchProducts]);

  const onSubmit = async (values: any) => {
    if (!priceListId) return;
    try {
      if (isEditMode) {
        const productId = Number(values.productId || selectedProduct.productId);
        const portId = Number(values.portId || selectedProduct.portId);
        const price = Number(values.price);
        const res: any = await updatePrice({
          priceListId: Number(priceListId),
          productId,
          portId,
          price,
        }).unwrap();
        showSuccessToast(res?.message);
      } else {
        const payload = {
          priceListId: Number(priceListId),
          productId: Number(values.productId),
          portId: Number(values.portId),
          price: Number(values.price),
        };
        const res: any = await addPrice(payload).unwrap();
        showSuccessToast(res?.message);
      }
      setOpenAddEditModal(false);
      setIsEditMode(false);
      fetchProducts({ priceListId });
    } catch (e) {
      // swallow; UI may have global error handler
    }
  };

  return (
    <Stack gap={1} sx={{ height: '100%' }}>
      <PageHeader options={breadcrumbOptions} actions={actions} />
      <PriceListDetailsInformation priceListInfo={priceListInfo} />
      <TableCardContent>
        <ClientSideAgGrid
          columnDefs={columns}
          rowData={rowData}
          loading={isLoading}
          className="price-list-detail-grid"
          infiniteRows={true}
        />
      </TableCardContent>

      <AddAndEditPriceModal
        open={openAddEditModal}
        onClose={() => setOpenAddEditModal(false)}
        control={control}
        handleSubmit={handleSubmit(onSubmit)}
        errors={errors}
        defaultValues={selectedProduct}
        productOptions={productOptions}
        portOptions={portOptions}
        submitting={isSubmitting}
        isEditMode={isEditMode}
      />
    </Stack>
  );
};

export default PriceListDetailContainer;
